// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 1eaff86a-efae-4cf3-be65-a08aec0afbb2

// ---------------------------------------------------------------------------
// Primordia — Saved Views & Report Builder
// ---------------------------------------------------------------------------
// Generalises the Inventory facet rail into a first-class, saveable query:
//
//   view = { id, name, description, type, filters[], sort[], columns[], shared }
//
// LeanIX calls this "Inventory Tools > Save as New". We build it in-app rather
// than bolting a BI tool on the side, because every query has to stay inside
// the client's own portal and respect tenant isolation — store.listFactSheets()
// is already tenant-scoped, so a saved view can never leak across engagements.
//
// Reports (chart definitions) are persisted in the same collection so a
// "view" and a "report" are just two kinds of saved query.
//
// Persistence: the API (GET/POST/DELETE /views) when it is up, otherwise
// localStorage under 'primordia.views.<tenantId>' so the feature is fully
// usable today. Same write-through philosophy as store.js.
//
// /views is NOT deployed today, so every save lands in localStorage and nothing
// crosses browsers. The UI must say that where the user can see it: the banner
// below states it, the tenant-wide sharing switch is disabled while the API is
// down, and saved items are badged "this device" rather than "team".
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, IconButton, Chip, Divider, Tabs, Tab,
  TextField, MenuItem, Checkbox, FormControlLabel, Switch, Alert, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, Grid, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Tooltip as ChartTooltip, Legend, Title,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  allFieldsFor, FACT_SHEET_TYPES, FIELD_TYPES, PICKLISTS, resolvePicklist, TAG_GROUPS,
} from './schema';
import { exportCsv, printElementToPdf } from './exports';
import { getConfig } from './config';
import { getIdToken } from './auth';
import store from './store';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

// Every Chart.js primitive this screen can draw (bars, arcs, both scales).
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, Legend, Title);

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const PALETTE = [
  '#0D5B9A', '#021F4B', '#2E7D32', '#F5A623', '#C62828',
  '#6A1B9A', '#00838F', '#EF6C00', '#5C6B7A', '#43A047',
  '#283593', '#8E24AA',
];
const headerSx = { '& th': { fontWeight: 700, color: HEAD, whiteSpace: 'nowrap' } };
const primaryBtn = { bgcolor: BLUE, '&:hover': { bgcolor: NAVY } };

// ---- Operators -------------------------------------------------------------
const OPERATORS = ['is', 'is not', 'contains', 'is empty', 'is not empty', 'greater than', 'less than'];
const VALUELESS = ['is empty', 'is not empty'];

// ---- Synthetic (non-schema) fields every fact sheet carries ----------------
const SYNTH_FIELDS = [
  { key: '__name', label: 'Name', type: FIELD_TYPES.TEXT, section: 'Core' },
  { key: '__type', label: 'Fact Sheet Type', type: FIELD_TYPES.SELECT, picklist: FACT_SHEET_TYPES.map((t) => t.key), section: 'Core' },
  { key: '__completion', label: 'Completion %', type: FIELD_TYPES.PERCENT, section: 'Core' },
  { key: '__quality', label: 'Quality Seal', type: FIELD_TYPES.SELECT, picklist: PICKLISTS.qualitySeal, section: 'Core' },
];

const MEASURES = [
  { key: 'count', label: 'Count of applications' },
  { key: 'tco', label: 'Sum of TCO' },
  { key: 'completion', label: 'Average completion' },
];

const CHART_TYPES = [
  { key: 'bar', label: 'Bar' },
  { key: 'hbar', label: 'Horizontal bar' },
  { key: 'doughnut', label: 'Doughnut' },
  { key: 'stacked', label: 'Stacked bar' },
];

const uid = (p) => `${p}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;

// ---- Sharing: honest labels for what each state actually does --------------
const SHARE_LABEL_LIVE = 'Shared with my team (tenant-wide)';
const SHARE_LABEL_LOCAL = 'Saved on this device only — team sharing unavailable';
const SHARE_TIP_LIVE = 'Everyone working in this tenant sees this item.';
const SHARE_TIP_LOCAL = 'Tenant-wide sharing needs the /views API, which is not deployed. '
  + 'Saves stay in this browser, so a colleague signing in sees nothing. '
  + 'Use Export CSV to hand the result to someone else.';

const BLANK_VIEW = () => ({
  id: null,
  kind: 'view',
  name: '',
  description: '',
  type: 'Application',
  filters: [],
  sort: [{ field: '__name', dir: 'asc' }],
  columns: ['__name', 'lifecycleState', 'timeClassification', '__completion'],
  shared: false,
});

const BLANK_REPORT = () => ({
  id: null,
  kind: 'report',
  name: '',
  description: '',
  type: 'Application',
  chartType: 'bar',
  groupBy: 'timeClassification',
  splitBy: '',
  measure: 'count',
  shared: false,
});

// ---- Value access ----------------------------------------------------------
function valueOf(fs, key) {
  if (!key) return undefined;
  if (key === '__name') return fs.name;
  if (key === '__type') return fs.type;
  if (key === '__completion') return fs.completion;
  if (key === '__quality') return fs.quality;
  if (key.indexOf('__tag:') === 0) {
    const group = key.slice(6);
    return (fs.tags || []).filter((t) => t.group === group).map((t) => t.value).join(', ');
  }
  const v = fs.fields ? fs.fields[key] : undefined;
  if ((v === undefined || v === null || v === '') && key === 'name') return fs.name;
  return v;
}

function display(v) {
  if (v === undefined || v === null || v === '') return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function matchesFilter(fs, f) {
  if (!f.field || !f.op) return true;
  const raw = valueOf(fs, f.field);
  const s = display(raw);
  const target = (f.value === undefined || f.value === null) ? '' : String(f.value);
  if (VALUELESS.indexOf(f.op) === -1 && target === '') return true; // half-built row = inactive
  switch (f.op) {
    case 'is': return s.toLowerCase() === target.toLowerCase();
    case 'is not': return s.toLowerCase() !== target.toLowerCase();
    case 'contains': return s.toLowerCase().indexOf(target.toLowerCase()) !== -1;
    case 'is empty': return s === '';
    case 'is not empty': return s !== '';
    case 'greater than': {
      const a = Number(raw); const b = Number(target);
      return (Number.isFinite(a) && Number.isFinite(b)) ? a > b : s.localeCompare(target) > 0;
    }
    case 'less than': {
      const a = Number(raw); const b = Number(target);
      return (Number.isFinite(a) && Number.isFinite(b)) ? a < b : s.localeCompare(target) < 0;
    }
    default: return true;
  }
}

function compareOn(a, b, key) {
  const va = valueOf(a, key);
  const vb = valueOf(b, key);
  const na = Number(va);
  const nb = Number(vb);
  if (Number.isFinite(na) && Number.isFinite(nb) && va !== '' && vb !== '') return na - nb;
  const sa = display(va);
  const sb = display(vb);
  if (sa === '' && sb !== '') return 1;   // blanks always sink
  if (sb === '' && sa !== '') return -1;
  return sa.localeCompare(sb);
}

// The full field catalogue for a type: synthetics, tag groups, then schema.
function buildFieldList(type, tagGroups) {
  const seen = {};
  const out = [];
  SYNTH_FIELDS.forEach((f) => { seen[f.key] = true; out.push(f); });
  tagGroups.forEach((g) => {
    const key = `__tag:${g}`;
    if (seen[key]) return;
    seen[key] = true;
    out.push({ key, label: `Tag: ${g}`, type: FIELD_TYPES.SELECT, section: 'Tags', tagGroup: g });
  });
  allFieldsFor(type).forEach((f) => {
    if (f.type === FIELD_TYPES.RELATION || f.type === FIELD_TYPES.LIFECYCLE) return;
    if (seen[f.key]) return;
    seen[f.key] = true;
    out.push(f);
  });
  return out;
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
// The tenant-wide switch only means something once /views is deployed. While it
// is not, the switch is disabled and says what actually happens instead.
function ShareSwitch({ live, checked, onChange }) {
  return (
    <Tooltip title={live ? SHARE_TIP_LIVE : SHARE_TIP_LOCAL}>
      <span>
        <FormControlLabel
          control={(
            <Switch
              size="small"
              disabled={!live}
              checked={live && !!checked}
              onChange={(e) => onChange(e.target.checked)}
            />
          )}
          label={(
            <span style={{ fontSize: 13, color: live ? undefined : GREY }}>
              {live ? SHARE_LABEL_LIVE : SHARE_LABEL_LOCAL}
            </span>
          )}
        />
      </span>
    </Tooltip>
  );
}

function SavedList({ items, activeId, emptyText, live, onPick, onDuplicate, onDelete }) {
  if (!items.length) {
    return <Typography variant="body2" sx={{ color: GREY, p: 2 }}>{emptyText}</Typography>;
  }
  return (
    <Box>
      {items.map((v) => (
        <Box
          key={v.id}
          sx={{
            display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee',
            bgcolor: v.id === activeId ? '#eef4fb' : 'transparent',
          }}
        >
          <Box onClick={() => onPick(v)} sx={{ flex: 1, minWidth: 0, p: 1, cursor: 'pointer' }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: HEAD }}>{v.name}</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                icon={live && v.shared ? <GroupsIcon sx={{ fontSize: 13 }} /> : <PersonIcon sx={{ fontSize: 13 }} />}
                label={live ? (v.shared ? 'team' : 'personal') : 'this device'}
                sx={{ height: 18, fontSize: 10 }}
              />
              <Typography noWrap variant="caption" sx={{ color: GREY }}>{v.type}</Typography>
            </Stack>
          </Box>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => onDuplicate(v)}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete(v)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
}

function FieldSelect({ label, value, fields, onChange, allowBlank, blankLabel, sx }) {
  return (
    <TextField select size="small" label={label} value={value || ''} sx={sx}
      onChange={(e) => onChange(e.target.value)}>
      {allowBlank && <MenuItem value="">{blankLabel || '— none —'}</MenuItem>}
      {fields.map((f) => (
        <MenuItem key={f.key} value={f.key}>
          {f.label}
          {f.section ? <span style={{ color: '#9AA6B2', fontSize: 11, marginLeft: 6 }}>{f.section}</span> : null}
        </MenuItem>
      ))}
    </TextField>
  );
}

// ---------------------------------------------------------------------------
export default function SavedViews({ tenantId, onOpen }) {
  const [tab, setTab] = useState(0);
  const [all, setAll] = useState([]);
  const [items, setItems] = useState([]);       // saved views + reports
  const [apiUp, setApiUp] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [draft, setDraft] = useState(BLANK_VIEW);
  const [report, setReport] = useState(BLANK_REPORT);
  const [colSearch, setColSearch] = useState('');
  const chartRef = useRef(null);

  const lsKey = `primordia.views.${tenantId}`;

  // ---- fact sheets (already tenant-scoped by the store) --------------------
  useEffect(() => {
    let live = true;
    store.listFactSheets(tenantId).then((rows) => { if (live) setAll(rows); });
    return () => { live = false; };
  }, [tenantId]);

  // ---- persistence --------------------------------------------------------
  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getIdToken()}`,
        ...(options.headers || {}),
      },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here — before the !res.ok check below could report the real
    // status — so a 502 surfaced as "Unexpected token <". Same guarded form as
    // AiEnrichment and RelationshipWizard.
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    return data;
  }, []);

  const readLocal = useCallback(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }, [lsKey]);

  const writeLocal = useCallback((list) => {
    try { localStorage.setItem(lsKey, JSON.stringify(list)); } catch (e) { /* quota */ }
  }, [lsKey]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const list = await api('/views');
        if (!Array.isArray(list)) throw new Error('unexpected /views payload');
        if (!live) return;
        setApiUp(true);
        setItems(list.filter((v) => !v.tenantId || v.tenantId === tenantId));
      } catch (e) {
        if (!live) return;
        setApiUp(false);
        setItems(readLocal());
      }
    })();
    return () => { live = false; };
  }, [api, tenantId, readLocal]);

  const persist = useCallback((next) => {
    setItems(next);
    writeLocal(next);
  }, [writeLocal]);

  const saveItem = useCallback((item) => {
    const record = { ...item, id: item.id || uid(item.kind), tenantId };
    const next = items.filter((i) => i.id !== record.id).concat([record])
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    persist(next);
    if (apiUp) {
      api('/views', { method: 'POST', body: JSON.stringify(record) }).catch(() => setApiUp(false));
    }
    return record;
  }, [items, persist, apiUp, api, tenantId]);

  const deleteItem = useCallback((item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    persist(items.filter((i) => i.id !== item.id));
    if (apiUp) {
      api(`/views?id=${encodeURIComponent(item.id)}&tenantId=${encodeURIComponent(tenantId)}`, { method: 'DELETE' })
        .catch(() => setApiUp(false));
    }
    setDraft((d) => (d.id === item.id ? { ...d, id: null } : d));
    setReport((r) => (r.id === item.id ? { ...r, id: null } : r));
    setMsg(`Deleted "${item.name}"`);
  }, [items, persist, apiUp, api, tenantId]);

  const views = useMemo(() => items.filter((i) => i.kind !== 'report'), [items]);
  const reports = useMemo(() => items.filter((i) => i.kind === 'report'), [items]);

  // ---- field catalogue ----------------------------------------------------
  const tagGroups = useMemo(() => {
    const found = {};
    TAG_GROUPS.forEach((g) => { found[g] = true; });
    all.forEach((fs) => (fs.tags || []).forEach((t) => { if (t.group) found[t.group] = true; }));
    return Object.keys(found);
  }, [all]);

  const viewFields = useMemo(() => buildFieldList(draft.type, tagGroups), [draft.type, tagGroups]);
  const viewFieldMap = useMemo(() => {
    const m = {};
    viewFields.forEach((f) => { m[f.key] = f; });
    return m;
  }, [viewFields]);

  const reportFields = useMemo(() => buildFieldList(report.type, tagGroups), [report.type, tagGroups]);

  // Values offered for a picklist-backed field (schema picklist, or the tag
  // values actually present in this tenant's data).
  // `useCallback` memoised the FUNCTION, not the answer — and this is called
  // once per filter row inside the render, on every keystroke. The tag-group
  // branch walked the entire inventory each time. Every distinct tag value is
  // collected in ONE pass over `all`, and only when `all` actually changes.
  const tagValuesByGroup = useMemo(() => {
    const out = {};
    all.forEach((fs) => (fs.tags || []).forEach((t) => {
      if (!t || !t.group) return;
      (out[t.group] = out[t.group] || new Set()).add(t.value);
    }));
    Object.keys(out).forEach((g) => { out[g] = [...out[g]].sort(); });
    return out;
  }, [all]);

  const optionsFor = useCallback((fieldKey) => {
    const f = viewFieldMap[fieldKey];
    if (!f) return [];
    if (f.tagGroup) return tagValuesByGroup[f.tagGroup] || [];
    if (f.type === FIELD_TYPES.BOOLEAN) return ['Yes', 'No'];
    if (f.picklist) return resolvePicklist(f.picklist);
    return [];
  }, [viewFieldMap, tagValuesByGroup]);

  // ---- live result set ----------------------------------------------------
  const rows = useMemo(() => {
    const base = all.filter((fs) => fs.type === draft.type);
    const filtered = base.filter((fs) => (draft.filters || []).every((f) => matchesFilter(fs, f)));
    const sorters = (draft.sort || []).filter((s) => s.field);
    if (!sorters.length) return filtered;
    return filtered.slice().sort((a, b) => {
      for (let i = 0; i < sorters.length; i += 1) {
        const c = compareOn(a, b, sorters[i].field);
        if (c !== 0) return sorters[i].dir === 'desc' ? -c : c;
      }
      return 0;
    });
  }, [all, draft.type, draft.filters, draft.sort]);

  // ---- draft mutators -----------------------------------------------------
  const patch = (p) => setDraft((d) => ({ ...d, ...p }));
  const patchReport = (p) => setReport((r) => ({ ...r, ...p }));

  const setFilter = (i, p) => patch({ filters: draft.filters.map((f, n) => (n === i ? { ...f, ...p } : f)) });
  const addFilter = () => patch({ filters: [...draft.filters, { field: '__name', op: 'contains', value: '' }] });
  const removeFilter = (i) => patch({ filters: draft.filters.filter((_, n) => n !== i) });

  const addSort = () => patch({ sort: [...draft.sort, { field: '__name', dir: 'asc' }] });
  const setSort = (i, p) => patch({ sort: draft.sort.map((s, n) => (n === i ? { ...s, ...p } : s)) });
  const removeSort = (i) => patch({ sort: draft.sort.filter((_, n) => n !== i) });
  const moveSort = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= draft.sort.length) return;
    const next = draft.sort.slice();
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
    patch({ sort: next });
  };

  const toggleColumn = (key) => patch({
    columns: draft.columns.indexOf(key) === -1
      ? [...draft.columns, key]
      : draft.columns.filter((c) => c !== key),
  });

  const changeType = (type) => setDraft({
    ...draft, type, filters: [], sort: [{ field: '__name', dir: 'asc' }],
    columns: ['__name', 'lifecycleState', '__completion'],
  });

  // ---- view actions -------------------------------------------------------
  const saveView = () => {
    if (!draft.name.trim()) { setErr('Give the view a name before saving.'); return; }
    setErr('');
    const saved = saveItem({ ...draft, kind: 'view' });
    setDraft(saved);
    setMsg(`Saved "${saved.name}"`);
  };
  const duplicateView = (v) => {
    const copy = saveItem({ ...v, id: null, name: `${v.name} (copy)` });
    setDraft(copy);
    setMsg(`Duplicated as "${copy.name}"`);
  };
  const openView = (v) => {
    setDraft({ ...BLANK_VIEW(), ...v });
    setErr(''); setMsg('');
  };

  const exportView = () => {
    const cols = draft.columns.map((key) => ({
      label: (viewFieldMap[key] && viewFieldMap[key].label) || key,
      value: (r) => display(valueOf(r, key)),
    }));
    exportCsv(`${(draft.name || 'view').replace(/\s+/g, '-').toLowerCase()}-${tenantId}.csv`, rows, cols);
  };

  // ---- report ------------------------------------------------------------
  const reportRows = useMemo(() => all.filter((fs) => fs.type === report.type), [all, report.type]);

  const agg = useMemo(() => {
    const groups = [];
    const groupIdx = {};
    const splitSeen = {};
    reportRows.forEach((fs) => {
      const g = display(valueOf(fs, report.groupBy)) || '(not set)';
      const s = report.splitBy ? (display(valueOf(fs, report.splitBy)) || '(not set)') : 'Value';
      splitSeen[s] = true;
      if (groupIdx[g] === undefined) {
        groupIdx[g] = groups.length;
        groups.push({ label: g, cells: {} });
      }
      const cells = groups[groupIdx[g]].cells;
      if (!cells[s]) cells[s] = { n: 0, sum: 0 };
      cells[s].n += 1;
      if (report.measure === 'tco') cells[s].sum += Number(valueOf(fs, 'tco')) || 0;
      if (report.measure === 'completion') cells[s].sum += Number(fs.completion) || 0;
    });
    const splits = Object.keys(splitSeen).sort();
    const valueOfCell = (c) => {
      if (!c) return 0;
      if (report.measure === 'count') return c.n;
      if (report.measure === 'tco') return Math.round(c.sum);
      return c.n ? Math.round(c.sum / c.n) : 0;
    };
    const table = groups.map((g) => {
      const values = splits.map((s) => valueOfCell(g.cells[s]));
      const total = report.measure === 'completion'
        ? Math.round(values.reduce((a, b) => a + b, 0) / (values.filter((v) => v > 0).length || 1))
        : values.reduce((a, b) => a + b, 0);
      return { label: g.label, values, total };
    }).sort((a, b) => b.total - a.total);
    return { splits, table };
  }, [reportRows, report.groupBy, report.splitBy, report.measure]);

  const measureLabel = (MEASURES.find((m) => m.key === report.measure) || MEASURES[0]).label;
  const fmtMeasure = (v) => {
    if (report.measure === 'tco') return `$ ${Number(v).toLocaleString()}`;
    if (report.measure === 'completion') return `${v}%`;
    return String(v);
  };

  const chartData = useMemo(() => {
    const labels = agg.table.map((r) => r.label);
    if (report.chartType === 'doughnut') {
      return {
        labels,
        datasets: [{ label: measureLabel, data: agg.table.map((r) => r.total), backgroundColor: PALETTE }],
      };
    }
    if (report.splitBy) {
      return {
        labels,
        datasets: agg.splits.map((s, i) => ({
          label: s,
          data: agg.table.map((r) => r.values[i]),
          backgroundColor: PALETTE[i % PALETTE.length],
        })),
      };
    }
    return {
      labels,
      datasets: [{ label: measureLabel, data: agg.table.map((r) => r.total), backgroundColor: PALETTE }],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agg, report.chartType, report.splitBy, report.measure]);

  const chartOptions = useMemo(() => {
    if (report.chartType === 'doughnut') {
      return { maintainAspectRatio: false, plugins: { legend: { position: 'right' } } };
    }
    const stacked = report.chartType === 'stacked';
    return {
      maintainAspectRatio: false,
      indexAxis: report.chartType === 'hbar' ? 'y' : 'x',
      plugins: { legend: { display: !!report.splitBy, position: 'top' } },
      scales: {
        x: { stacked, beginAtZero: true },
        y: { stacked, beginAtZero: true },
      },
    };
  }, [report.chartType, report.splitBy]);

  const saveReport = () => {
    if (!report.name.trim()) { setErr('Give the report a name before saving.'); return; }
    setErr('');
    const saved = saveItem({ ...report, kind: 'report' });
    setReport(saved);
    setMsg(`Saved "${saved.name}"`);
  };
  const duplicateReport = (r) => {
    const copy = saveItem({ ...r, id: null, name: `${r.name} (copy)` });
    setReport(copy);
    setMsg(`Duplicated as "${copy.name}"`);
  };
  const openReport = (r) => {
    setReport({ ...BLANK_REPORT(), ...r });
    setErr(''); setMsg('');
  };

  const exportReportCsv = () => {
    const cols = [{ label: 'Group', value: (r) => r.label }]
      .concat(agg.splits.map((s, i) => ({ label: s, value: (r) => r.values[i] })))
      .concat([{ label: 'Total', value: (r) => r.total }]);
    exportCsv(`${(report.name || 'report').replace(/\s+/g, '-').toLowerCase()}-${tenantId}.csv`, agg.table, cols);
  };

  // The print window only receives innerHTML, so the live <canvas> would come
  // out blank — snapshot it into an <img> first.
  const exportReportPdf = () => {
    const img = document.getElementById('report-chart-img');
    if (img && chartRef.current && chartRef.current.toBase64Image) {
      try { img.src = chartRef.current.toBase64Image(); } catch (e) { /* ignore */ }
    }
    printElementToPdf('report-print', report.name || 'Primordia — Report');
  };

  const visibleColumnFields = useMemo(
    () => draft.columns.map((k) => viewFieldMap[k]).filter(Boolean),
    [draft.columns, viewFieldMap],
  );

  const columnChoices = useMemo(() => {
    const q = colSearch.trim().toLowerCase();
    if (!q) return viewFields;
    return viewFields.filter((f) => f.label.toLowerCase().indexOf(q) !== -1);
  }, [viewFields, colSearch]);

  // Group-by / split-by candidates: select-type fields, tags, and core dimensions.
  const dimensionFields = useMemo(
    () => reportFields.filter(
      (f) => f.type === FIELD_TYPES.SELECT || f.tagGroup || f.type === FIELD_TYPES.BOOLEAN,
    ),
    [reportFields],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Saved Views &amp; Reports</Typography>
        <HelpBubble topic="savedViews" />
        <Chip size="small" label={tenantId} sx={{ bgcolor: NAVY, color: '#fff', height: 20 }} />
      </Stack>

      {!apiUp && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Views and reports are saved in this browser only — the <code>/views</code> API is not deployed.
          Nobody else sees what you save here, not even a colleague on <b>{tenantId}</b>, and the saves go
          when this browser&apos;s site data is cleared. Export to CSV or PDF to share a result.
        </Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label={<TabLabel text="Views" topic="tabSavedViewsList" />} sx={{ fontWeight: 700 }} />
        <Tab label={<TabLabel text="Report builder" topic="tabReportBuilder" />} sx={{ fontWeight: 700 }} />
      </Tabs>

      {/* ------------------------------------------------------------ VIEWS */}
      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 0, alignSelf: 'flex-start' }}>
              <Stack direction="row" alignItems="center" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700, color: HEAD }}>Saved views</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setDraft(BLANK_VIEW())}>New</Button>
              </Stack>
              <Divider />
              <SavedList
                items={views}
                activeId={draft.id}
                emptyText="No saved views yet. Build one on the right and press Save."
                live={apiUp}
                onPick={openView}
                onDuplicate={duplicateView}
                onDelete={deleteItem}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField size="small" label="View name" value={draft.name} sx={{ minWidth: 220 }}
                  onChange={(e) => patch({ name: e.target.value })} />
                <TextField size="small" label="Description" value={draft.description} sx={{ flex: 1 }}
                  onChange={(e) => patch({ description: e.target.value })} />
                <TextField select size="small" label="Fact sheet type" value={draft.type} sx={{ minWidth: 180 }}
                  onChange={(e) => changeType(e.target.value)}>
                  {FACT_SHEET_TYPES.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                </TextField>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                <ShareSwitch live={apiUp} checked={draft.shared} onChange={(v) => patch({ shared: v })} />
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<SaveIcon />} variant="contained" sx={primaryBtn} onClick={saveView}>
                  {draft.id ? 'Update view' : 'Save as new'}
                </Button>
                <Button size="small" startIcon={<ContentCopyIcon />} variant="outlined"
                  disabled={!draft.id} onClick={() => duplicateView(draft)}>Duplicate</Button>
                <Button size="small" startIcon={<DeleteIcon />} variant="outlined" color="error"
                  disabled={!draft.id} onClick={() => deleteItem(draft)}>Delete</Button>
                <Button size="small" startIcon={<DownloadIcon />} variant="outlined"
                  disabled={!draft.columns.length} onClick={exportView}>Export CSV</Button>
              </Stack>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Filters */}
              <Grid item xs={12} lg={7}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ flex: 1, color: HEAD, fontWeight: 700 }}>Filters</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addFilter}>Add filter</Button>
                  </Stack>
                  {!draft.filters.length && (
                    <Typography variant="body2" sx={{ color: GREY }}>No filters — every {draft.type} fact sheet is included.</Typography>
                  )}
                  {draft.filters.map((f, i) => {
                    const opts = optionsFor(f.field);
                    const needsValue = VALUELESS.indexOf(f.op) === -1;
                    return (
                      <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <FieldSelect
                          label="Field" value={f.field} fields={viewFields} sx={{ flex: 1.4, minWidth: 150 }}
                          onChange={(v) => setFilter(i, { field: v, value: '' })}
                        />
                        <TextField select size="small" label="Operator" value={f.op} sx={{ width: 150 }}
                          onChange={(e) => setFilter(i, { op: e.target.value })}>
                          {OPERATORS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                        </TextField>
                        {needsValue && (opts.length ? (
                          <TextField select size="small" label="Value" value={f.value || ''} sx={{ flex: 1, minWidth: 140 }}
                            onChange={(e) => setFilter(i, { value: e.target.value })}>
                            <MenuItem value="">— any —</MenuItem>
                            {opts.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                          </TextField>
                        ) : (
                          <TextField size="small" label="Value" value={f.value || ''} sx={{ flex: 1, minWidth: 140 }}
                            onChange={(e) => setFilter(i, { value: e.target.value })} />
                        ))}
                        {!needsValue && <Box sx={{ flex: 1 }} />}
                        <IconButton size="small" onClick={() => removeFilter(i)}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                      </Stack>
                    );
                  })}

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ flex: 1, color: HEAD, fontWeight: 700 }}>Sort scheme</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addSort}>Add level</Button>
                  </Stack>
                  {draft.sort.map((s, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip size="small" label={i + 1} sx={{ height: 20, bgcolor: 'chipMuted.main', color: HEAD, fontWeight: 700 }} />
                      <FieldSelect label="Sort by" value={s.field} fields={viewFields} sx={{ flex: 1, minWidth: 160 }}
                        onChange={(v) => setSort(i, { field: v })} />
                      <TextField select size="small" label="Direction" value={s.dir} sx={{ width: 130 }}
                        onChange={(e) => setSort(i, { dir: e.target.value })}>
                        <MenuItem value="asc">Ascending</MenuItem>
                        <MenuItem value="desc">Descending</MenuItem>
                      </TextField>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton size="small" disabled={i === 0} onClick={() => moveSort(i, -1)}>
                            <ArrowUpwardIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton size="small" disabled={i === draft.sort.length - 1} onClick={() => moveSort(i, 1)}>
                            <ArrowDownwardIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton size="small" onClick={() => removeSort(i)}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Stack>
                  ))}
                  {!draft.sort.length && (
                    <Typography variant="body2" sx={{ color: GREY }}>No sort levels — results keep store order.</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Columns */}
              <Grid item xs={12} lg={5}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="overline" sx={{ color: HEAD, fontWeight: 700 }}>Columns</Typography>
                  <TextField
                    size="small" fullWidth placeholder="Find a field…" value={colSearch} sx={{ my: 1 }}
                    onChange={(e) => setColSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  />
                  <Typography variant="caption" sx={{ color: GREY }}>
                    {draft.columns.length} selected of {viewFields.length} fields
                  </Typography>
                  <Box sx={{ maxHeight: 330, overflow: 'auto', mt: 1, border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                    {columnChoices.map((f) => (
                      <Box key={f.key} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={(
                            <Checkbox size="small" checked={draft.columns.indexOf(f.key) !== -1}
                              onChange={() => toggleColumn(f.key)} />
                          )}
                          label={<span style={{ fontSize: 13 }}>{f.label}</span>}
                        />
                      </Box>
                    ))}
                    {!columnChoices.length && (
                      <Typography variant="body2" sx={{ color: GREY, p: 1 }}>No field matches “{colSearch}”.</Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Live results */}
            <Paper>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>Results</Typography>
                <Chip size="small" label={`${rows.length} of ${all.filter((fs) => fs.type === draft.type).length}`} />
                <Typography variant="caption" sx={{ color: GREY }}>click a row to open the fact sheet</Typography>
              </Stack>
              <Divider />
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headerSx}>
                      {visibleColumnFields.map((f) => <TableCell key={f.key}>{f.label}</TableCell>)}
                      {!visibleColumnFields.length && <TableCell>Name</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.slice(0, 300).map((fs) => (
                      <TableRow key={fs.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen && onOpen(fs.id)}>
                        {visibleColumnFields.map((f) => (
                          <TableCell key={f.key} sx={{ fontWeight: f.key === '__name' ? 600 : 400 }}>
                            {display(valueOf(fs, f.key)) || '—'}
                          </TableCell>
                        ))}
                        {!visibleColumnFields.length && <TableCell sx={{ fontWeight: 600 }}>{fs.name}</TableCell>}
                      </TableRow>
                    ))}
                    {!rows.length && (
                      <TableRow>
                        <TableCell colSpan={Math.max(1, visibleColumnFields.length)} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                          No fact sheets match these filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              {rows.length > 300 && (
                <Typography variant="caption" sx={{ color: GREY, p: 1.5, display: 'block' }}>
                  Showing the first 300 rows — export to CSV for the full {rows.length}.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* --------------------------------------------------- REPORT BUILDER */}
      {tab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper>
              <Stack direction="row" alignItems="center" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700, color: HEAD }}>Saved reports</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setReport(BLANK_REPORT())}>New</Button>
              </Stack>
              <Divider />
              <SavedList
                items={reports}
                activeId={report.id}
                emptyText="No saved reports yet."
                live={apiUp}
                onPick={openReport}
                onDuplicate={duplicateReport}
                onDelete={deleteItem}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField size="small" label="Report name" value={report.name} sx={{ minWidth: 220 }}
                  onChange={(e) => patchReport({ name: e.target.value })} />
                <TextField size="small" label="Description" value={report.description} sx={{ flex: 1 }}
                  onChange={(e) => patchReport({ description: e.target.value })} />
                <TextField select size="small" label="Fact sheet type" value={report.type} sx={{ minWidth: 170 }}
                  onChange={(e) => patchReport({ type: e.target.value, groupBy: 'lifecycleState', splitBy: '' })}>
                  {FACT_SHEET_TYPES.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                </TextField>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField select size="small" label="Chart type" value={report.chartType} sx={{ minWidth: 170 }}
                  onChange={(e) => patchReport({ chartType: e.target.value })}>
                  {CHART_TYPES.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                </TextField>
                <FieldSelect label="Group by" value={report.groupBy} fields={dimensionFields} sx={{ flex: 1, minWidth: 180 }}
                  onChange={(v) => patchReport({ groupBy: v })} />
                <FieldSelect label="Split by (optional)" value={report.splitBy} fields={dimensionFields}
                  allowBlank blankLabel="— no split —" sx={{ flex: 1, minWidth: 180 }}
                  onChange={(v) => patchReport({ splitBy: v })} />
                <TextField select size="small" label="Measure" value={report.measure} sx={{ minWidth: 190 }}
                  onChange={(e) => patchReport({ measure: e.target.value })}>
                  {MEASURES.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
                </TextField>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                <ShareSwitch live={apiUp} checked={report.shared} onChange={(v) => patchReport({ shared: v })} />
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<SaveIcon />} variant="contained" sx={primaryBtn} onClick={saveReport}>
                  {report.id ? 'Update report' : 'Save as new'}
                </Button>
                <Button size="small" startIcon={<ContentCopyIcon />} variant="outlined"
                  disabled={!report.id} onClick={() => duplicateReport(report)}>Duplicate</Button>
                <Button size="small" startIcon={<DeleteIcon />} variant="outlined" color="error"
                  disabled={!report.id} onClick={() => deleteItem(report)}>Delete</Button>
                <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportReportCsv}>Export CSV</Button>
                <Button size="small" startIcon={<PictureAsPdfIcon />} variant="outlined" onClick={exportReportPdf}>PDF</Button>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>
                {report.name || 'Untitled report'} — {measureLabel}
              </Typography>
              <Box sx={{ height: 340 }}>
                {report.chartType === 'doughnut'
                  ? <Doughnut ref={chartRef} data={chartData} options={chartOptions} />
                  : <Bar ref={chartRef} data={chartData} options={chartOptions} />}
              </Box>
              <Typography variant="caption" sx={{ color: GREY }}>
                {reportRows.length} {report.type} fact sheets · {agg.table.length} groups
                {report.splitBy ? ` · ${agg.splits.length} series` : ''}
              </Typography>
            </Paper>

            <Paper>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, p: 1.5 }}>Underlying data</Typography>
              <Divider />
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headerSx}>
                      <TableCell>Group</TableCell>
                      {report.splitBy && agg.splits.map((s) => <TableCell key={s} align="right">{s}</TableCell>)}
                      <TableCell align="right">{measureLabel}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agg.table.map((r) => (
                      <TableRow key={r.label} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.label}</TableCell>
                        {report.splitBy && r.values.map((v, i) => (
                          <TableCell key={agg.splits[i]} align="right">{fmtMeasure(v)}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700, color: HEAD }}>{fmtMeasure(r.total)}</TableCell>
                      </TableRow>
                    ))}
                    {!agg.table.length && (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                          Nothing to chart for this type.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>

            {/* Print-only payload for printElementToPdf (innerHTML is copied). */}
            <Box id="report-print" sx={{ display: 'none' }}>
              <h1>{report.name || 'Primordia — Report'}</h1>
              <div className="row"><span className="lbl">Tenant</span><span className="val">{tenantId}</span></div>
              <div className="row"><span className="lbl">Measure</span><span className="val">{measureLabel}</span></div>
              <div className="row"><span className="lbl">Group by</span><span className="val">{report.groupBy}</span></div>
              {report.splitBy && (
                <div className="row"><span className="lbl">Split by</span><span className="val">{report.splitBy}</span></div>
              )}
              {report.description && (
                <div className="row"><span className="lbl">Description</span><span className="val">{report.description}</span></div>
              )}
              <h2>Chart</h2>
              <img id="report-chart-img" alt="" style={{ width: '100%', maxWidth: 760 }} />
              <h2>Data</h2>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Group</th>
                    {report.splitBy && agg.splits.map((s) => (
                      <th key={s} style={{ textAlign: 'right', borderBottom: '1px solid #ccc' }}>{s}</th>
                    ))}
                    <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc' }}>{measureLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {agg.table.map((r) => (
                    <tr key={r.label}>
                      <td style={{ borderBottom: '1px solid #eee' }}>{r.label}</td>
                      {report.splitBy && r.values.map((v, i) => (
                        <td key={agg.splits[i]} style={{ textAlign: 'right', borderBottom: '1px solid #eee' }}>{fmtMeasure(v)}</td>
                      ))}
                      <td style={{ textAlign: 'right', borderBottom: '1px solid #eee' }}>{fmtMeasure(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
